import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Play, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertTriangle,
  Pause,
  RefreshCw,
  Calendar,
  Timer,
  FileText,
  Sparkles
} from 'lucide-react';

const JobMonitor = () => {
  const [jobs, setJobs] = useState([]);
  const [lockStatus, setLockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);

  useEffect(() => {
    fetchJobStatus();
    const interval = setInterval(fetchJobStatus, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchJobStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/scheduler/status`);
      // Filter to show only relevant jobs
      const relevantJobs = response.data.jobs.filter(j => 
        ['demand_etl', 'procurement_intelligence'].includes(j.job_id)
      );
      setJobs(relevantJobs);
      setLockStatus(response.data.lock_status);
    } catch (error) {
      console.error('Error fetching job status:', error);
    } finally {
      setLoading(false);
    }
  };

  const runJob = async (jobId) => {
    setRunningJob(jobId);
    try {
      const response = await axios.post(`${API}/admin/scheduler/${jobId}/run`);
      if (response.data.success) {
        toast.success(`Job ${jobId} executado com sucesso`);
        fetchJobStatus();
      } else {
        toast.error(response.data.result?.message || 'Erro ao executar job');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar job');
    } finally {
      setRunningJob(null);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'running':
        return <Badge className="bg-blue-500"><Loader2 className="h-3 w-3 mr-1 animate-spin" />A executar</Badge>;
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Concluído</Badge>;
      case 'failed':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Erro</Badge>;
      case 'idle':
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Inativo</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Desconhecido'}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          A carregar...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-slate-600" />
            Automação Procurement AI
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={fetchJobStatus}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-slate-500">
          ETL diário às 06:30 → Intelligence em modo suggest
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {jobs.map((job) => (
          <div 
            key={job.job_id} 
            className="border rounded-lg p-4 space-y-3"
            data-testid={`job-${job.job_id}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {job.job_id === 'demand_etl' ? (
                  <FileText className="h-5 w-5 text-blue-500" />
                ) : (
                  <Sparkles className="h-5 w-5 text-purple-500" />
                )}
                <span className="font-medium">{job.name}</span>
              </div>
              {getStatusBadge(job.last_run?.status || job.status)}
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-slate-500 text-xs">Última Execução</p>
                <p className="font-medium">
                  {formatDateTime(job.last_run?.started_at)}
                </p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Próxima Execução</p>
                <p className="font-medium">
                  {job.job_id === 'procurement_intelligence' 
                    ? 'Após ETL' 
                    : formatDateTime(job.next_run)}
                </p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Duração</p>
                <p className="font-medium flex items-center gap-1">
                  <Timer className="h-3 w-3" />
                  {formatDuration(job.last_run?.duration_seconds)}
                </p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Resultado</p>
                {job.last_run?.summary ? (
                  <p className="font-medium text-green-600">
                    {job.job_id === 'demand_etl' 
                      ? `${job.last_run.summary.lines_saved?.toLocaleString() || 0} linhas`
                      : `${job.last_run.summary.suggestions_created || 0} sugestões`
                    }
                  </p>
                ) : job.last_run?.error ? (
                  <p className="font-medium text-red-600 text-xs truncate" title={job.last_run.error}>
                    {job.last_run.error.substring(0, 40)}...
                  </p>
                ) : (
                  <p className="text-slate-400">-</p>
                )}
              </div>
            </div>

            {/* Error Message */}
            {job.last_run?.error && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {job.last_run.error}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Button
                size="sm"
                variant="outline"
                onClick={() => runJob(job.job_id)}
                disabled={runningJob === job.job_id || job.status === 'running'}
                data-testid={`run-${job.job_id}`}
              >
                {runningJob === job.job_id ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-1" />
                )}
                Executar Agora
              </Button>
              
              <Badge 
                variant={job.enabled !== false ? 'default' : 'secondary'}
                className={job.enabled !== false ? 'bg-green-100 text-green-700' : ''}
              >
                {job.enabled !== false ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
          </div>
        ))}

        {/* Lock Status */}
        {lockStatus?.held && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
            <div className="flex items-center gap-2 text-yellow-700">
              <Pause className="h-4 w-4" />
              <span className="font-medium">Lock ativo</span>
            </div>
            <p className="text-yellow-600 text-xs mt-1">
              Job &quot;{lockStatus.job_name}&quot; em execução desde {formatDateTime(lockStatus.acquired_at)}
            </p>
          </div>
        )}

        {/* Help Text */}
        <p className="text-xs text-slate-400 text-center pt-2">
          O ETL importa documentos do SAGE (FT, FR, ISL, CPR) e o Intelligence
          gera sugestões de alteração de parâmetros. Ambos correm automaticamente às 06:30.
        </p>
      </CardContent>
    </Card>
  );
};

export default JobMonitor;
